import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { TemplateColorProperties } from '@lumen5/luminary';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';
import { isPaletteActive } from '../video-state/serialization/videoSerializer';

import { VideoStateTool } from './VideoStateTool';

import {
    getPaletteColorsAsHex,
    getTemplateColorPalettes,
} from 'js/src/data/templates/templates-selectors';
import type { RootState } from 'js/src/libs/RootState';
import { setColorPaletteReferenceOperation } from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';

export const setColorPaletteSchema = z.object({
    palette_name: z
        .string()
        .describe(
            "The exact name of a named color palette from the template (see templatePalettes in the video state). Applies the palette's colors to every active color slot.",
        ),
});
export type SetColorPaletteArgs = z.infer<typeof setColorPaletteSchema>;

export class SetColorPaletteTool extends VideoStateTool<typeof setColorPaletteSchema> {
    protected override readonly schema = setColorPaletteSchema;
    protected override readonly toolName = TOOL_NAMES.setColorPalette;
    public override readonly subagentAccess = SubagentAccess.BLOCKED;
    protected override readonly toolDescription =
        "Apply one of the template's named color palettes to the whole video, replacing every active color slot with the palette's colors.";

    public override getToolDisplayName(): string {
        return 'Setting color palette';
    }

    public get statusIcon(): string {
        return 'palette';
    }

    public async execute(args: SetColorPaletteArgs, context: ToolExecutionContext) {
        const { palette_name } = args;

        const palettes = getTemplateColorPalettes(this.getState(), this.luminary.template.id);
        if (!palettes || palettes.length === 0) {
            return 'Error: This template has no named color palettes to apply.';
        }

        const availableList = palettes
            .map((palette) => `${palette.name}: ${getPaletteColorsAsHex(palette).join(', ')}`)
            .join('; ');

        const match = palettes.find(
            (palette) => palette.name.toLowerCase() === palette_name.toLowerCase(),
        );
        if (!match) {
            return `Error: No palette named "${palette_name}". Available palettes: ${availableList}`;
        }

        const paletteColors = match.colors.map((color) => color[TemplateColorProperties.COLOR]);
        if (isPaletteActive(paletteColors, this.luminary.template.colors)) {
            return `No change: palette "${match.name}" is already active.`;
        }

        const { setColorPaletteReference } = makeOperationsCallable(
            { setColorPaletteReferenceOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );
        setColorPaletteReference(match.name, false, context.parentActionGroup);

        return `Success: Applied color palette "${match.name}" to every active color slot.`;
    }
}
